import {plural, t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import {useSelector} from "react-redux";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {styles as typographyStyles} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries} from "@khan/shared-styles";

import {getKaid} from "#data-access/profile-utils.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedTimeoutSpinner from "#shared-components-package/deprecated-timeout-spinner.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import {compactStyles} from "../shared-styles.ts";
import type {FeedbackType, ServerPost, ViewMode} from "../types.ts";
import {getAnswerInputId} from "../util.tsx";

import DiscussionInput from "./discussion-input.tsx";
import ConnectedDiscussionPost from "./discussion-post.tsx";
import type {State as StoreState} from "./discussion-reducer.ts";

type Props = {
    // DiscussionPostOwnProps
    answers: Array<string> | null | undefined;
    answerCount: number;
    topLevelPostKey: string;
    topicSlug: string;
    newPostKey: string | null | undefined;
    resetNewPostKeyInfo: () => void;
    tab: FeedbackType;
    displayInputPostKey?: string | null | undefined;
    restrictPosting: boolean;
    isLocked: boolean;
    clarificationsEnabled?: boolean;
    viewMode: ViewMode;
    authorKaid: string;
    moderatorMode?: boolean;
    isMaximized?: boolean;
    // DiscussionPostReduxProps
    onShowAnswers: () => void;
    // WithUserProfileProps
    profile: ProfileModelType | null;
    // Own props
    onAnswerPostSuccess: (post: ServerPost, tab: FeedbackType) => void;
    isCompactView: boolean;
    nodes: Record<string, HTMLLIElement | null | undefined>;
    expandAnswers: (postKey: string) => void;
    collapseAnswers: (postKey: string) => void;
    onToggleFormattingOptionsExpanded: (expanded: boolean) => void;
    onTogglePreviewExpanded: (expanded: boolean) => void;
    isExpanded: boolean;
    displayReplyInput: boolean;
    // to calm Flow
    children?: React.ReactNode;
};

export const AnswerList = (props: Props): React.ReactElement | null => {
    const status: "loading" | "loaded" | "failed" | null = useSelector(
        (state: StoreState) =>
            state.fetchingAnswersStatus[props.topLevelPostKey],
    );
    const contentKind = useSelector((state: StoreState) => state.contentKind);

    // We know that only the "questions" and "projecthelp" tabs can have answers,
    // so let's just skip this whole method if we're looking at a different tab.
    if (props.tab !== "questions" && props.tab !== "projecthelp") {
        return null;
    }

    // If there are no answers, we typically render an input field instead,
    // but users who can't post even if they're logged in shouldn't see that input field.
    // we check answerCount *and* answers.length because deleted answers are
    // counted, but not included in the answers array.
    if (
        (!props.answerCount || !props.answers || !props.answers.length) &&
        props.restrictPosting
    ) {
        return null;
    }

    const handleExpand = () => {
        props.expandAnswers(props.topLevelPostKey);
    };
    const handleCollapse = () => {
        props.collapseAnswers(props.topLevelPostKey);
    };

    // We know that there are answers at this point, but TypeScript does not, because
    // TypeScript is terrible.
    const answers = props.answers || [];

    const displayReplyInput =
        props.displayInputPostKey === props.topLevelPostKey;

    const replyInputField = (
        <div
            className={css(
                styles.answerInputFieldContainer,
                displayReplyInput && styles.answerInputFieldContainerExpanded,
            )}
            id={getAnswerInputId(props.topLevelPostKey)}
        >
            {props.tab === "projecthelp" &&
                // TODO(diedra): Get actual design from designers/pms.
                (getKaid(props.profile) === props.authorKaid
                    ? t`By answering your own post, your request won't be shown in the help requests list. If you want to clarify instead, you can edit your original post.`
                    : null)}
            {/* @ts-expect-error - TS2741 - Property 'acceptingClarification' is missing in type '{ isAnswer: true; autoFocus: boolean; tab: "questions" | "projecthelp"; clarificationsEnabled: boolean | undefined; onPostSuccess: (post: ServerPost, tab: FeedbackType) => void; ... 5 more ...; contentKind: string; }' but required in type 'ExportProps'. */}
            <DiscussionInput
                isAnswer={true}
                autoFocus={displayReplyInput}
                tab={props.tab}
                clarificationsEnabled={props.clarificationsEnabled}
                onPostSuccess={props.onAnswerPostSuccess}
                topicSlug={props.topicSlug}
                feedbackKey={props.topLevelPostKey}
                onToggleFormattingOptionsExpanded={
                    props.onToggleFormattingOptionsExpanded
                }
                onTogglePreviewExpanded={props.onTogglePreviewExpanded}
                showAvatar={!props.isCompactView}
                contentKind={contentKind}
            />
        </div>
    );

    if (props.answers && props.answers.length && props.answerCount !== 0) {
        // If this post has its answers expanded, we show all the answers.
        // Otherwise, we only show the first one.
        const filteredAnswers = props.isExpanded ? answers : [answers[0]];

        // Before we query for the answers, we don't know how many there are.
        // We use the answerCount, which includes deleted answers. Once we've
        // loaded the answers, we can use the length of the answers array.
        const knownAnswerCount =
            status === "loaded"
                ? props.answers?.length || 0
                : props.answerCount;

        return (
            <div
                className={css(
                    styles.answersList,
                    props.viewMode === "compact" &&
                        compactStyleSheet.answersList,
                )}
            >
                {!props.restrictPosting && !props.isLocked && replyInputField}
                <ul>
                    {filteredAnswers.map((answerKey, index) => (
                        <li key={answerKey} className={css(styles.answer)}>
                            {/* TODO(jeff, LP-6511)
                             * @ts-expect-error - TS2739 - Type '{ tab: FeedbackType; postKey: string; newPostKey: string | null | undefined; resetNewPostKeyInfo: () => void; isAnswer: true; getRef: (node: any) => any; topicSlug: string; restrictPosting: boolean; moderatorMode: boolean | undefined; viewMode: ViewMode; isMaximized: boolean | undefined; autoFocusPostOnMount: boolea...' is missing the following properties from type 'ExportProps': post, showPostedIn, showParentQuestion */}
                            <ConnectedDiscussionPost
                                tab={props.tab}
                                postKey={answerKey}
                                newPostKey={props.newPostKey}
                                resetNewPostKeyInfo={props.resetNewPostKeyInfo}
                                isAnswer={true}
                                getRef={(node) =>
                                    (props.nodes[answerKey] = node)
                                }
                                topicSlug={props.topicSlug}
                                restrictPosting={props.restrictPosting}
                                moderatorMode={props.moderatorMode}
                                viewMode={props.viewMode}
                                isMaximized={props.isMaximized}
                                // We only want to autoFocus the post on load if
                                // it's the second one in the list. That's because
                                // when the full list of answers is revealed, we
                                // want to highlight the first newly visible post.
                                autoFocusPostOnMount={index === 1}
                            />
                            {
                                // show button above answers
                                // if there are more than 2 (requires scrolling)
                                index === 0 &&
                                    status === "loaded" &&
                                    props.isExpanded &&
                                    knownAnswerCount > 2 &&
                                    filteredAnswers.length && (
                                        <ExpandCollapseButton
                                            answerCount={knownAnswerCount}
                                            onShowAnswers={() =>
                                                props.onShowAnswers()
                                            }
                                            status={status}
                                            expanded={props.isExpanded}
                                            handleExpand={() => handleExpand()}
                                            handleCollapse={() =>
                                                handleCollapse()
                                            }
                                        />
                                    )
                            }
                        </li>
                    ))}
                </ul>
                {status === "loading" ? (
                    <View style={[styles.loading, styles.expandCollapseButton]}>
                        <DeprecatedTimeoutSpinner
                            size="xsmall"
                            style={styles.spinner}
                            sentryId="discussion_replies_load"
                            description="Loading discussion post replies"
                        />
                        <span className={css(typographyStyles.LabelLarge)}>
                            {t`Loading...`}
                        </span>
                    </View>
                ) : (
                    <ExpandCollapseButton
                        answerCount={knownAnswerCount}
                        onShowAnswers={() => props.onShowAnswers()}
                        status={status}
                        expanded={props.isExpanded}
                        handleExpand={() => handleExpand()}
                        handleCollapse={() => handleCollapse()}
                    />
                )}
            </div>
        );
    }
    return (
        <div
            className={css(
                styles.answersList,
                props.isCompactView && compactStyleSheet.answersList,
                !props.displayInputPostKey && styles.displayNone,
            )}
        >
            {replyInputField}
        </div>
    );
};

const ExpandCollapseButton = (props: {
    answerCount: number;
    onShowAnswers: () => void;
    status: string | null | undefined;
    expanded: boolean;
    handleExpand: () => void;
    handleCollapse: () => void;
}): React.ReactElement | null => {
    const numAnswersHidable = props.answerCount - 1;

    if (numAnswersHidable < 1) {
        return null;
    }

    const getCopy = () => {
        if (props.status === "loaded") {
            if (props.expanded) {
                return plural(numAnswersHidable, {
                    one: "Hide # answer",
                    other: "Hide # answers",
                });
            }
            return plural(numAnswersHidable, {
                one: "See # more answer",
                other: "See # more answers",
            });
        }
        return t`Show more...`;
    };

    return (
        <Button
            kind="tertiary"
            style={styles.expandCollapseButton}
            onClick={() => {
                if (!props.expanded) {
                    props.onShowAnswers();
                    props.handleExpand();
                } else {
                    props.handleCollapse();
                }
            }}
        >
            {getCopy()}
        </Button>
    );
};

const compactStyleSheet = StyleSheet.create(compactStyles);

const styles = StyleSheet.create({
    answersList: {
        marginLeft: 68,

        [mediaQueries.xsOrSmaller]: compactStyles.answersList,
    },
    answerInputFieldContainer: {
        display: "none",
    },
    answerInputFieldContainerExpanded: {
        margin: "20px 0 28px 0",
        display: "block",
    },
    answer: {
        marginTop: 24,
    },
    spinner: {
        marginRight: spacing.xSmall_8,
    },
    displayNone: {
        display: "none",
    },
    expandCollapseButton: {
        marginTop: 24,
        marginBottom: 8,
    },
    loading: {
        flexDirection: "row",
        paddingTop: 0,
        minHeight: 48,
        alignItems: "center",
    },
});

export default AnswerList;
